/**
 * Fallbacks for authored rich text arriving at a surface that cannot fully show it.
 *
 * Question and task wording is authored in a rich text editor, so it reaches the client as
 * HTML. Two things about that HTML need handling before a surface displays it:
 *
 * - Images are stored as `<img src="MEDIA_ID">`, a bare id rather than a URL. Only a
 *   caller holding the record's `mediaResponses` map can turn those into something the
 *   browser can load, via `replaceMediaIdsWithUrls`. A caller without the map must remove
 *   the images, or the browser resolves the id against the current page and draws a broken
 *   image.
 * - A plain-text surface — a table cell, a one-line summary — cannot render tags at all,
 *   and printing the HTML shows the markup to the reader.
 *
 * These are last resorts. A surface that can resolve media and render HTML should do both
 * and not come here.
 */

/** Matches an `<img>` tag. Authored HTML carries no `>` inside an attribute value. */
const IMAGE_TAG = /<img\b[^>]*>/gi;

/**
 * A paragraph left with nothing in it, which an image's removal can produce.
 *
 * Matches the opening tag's attributes: an editor writes `<p class="...">` as readily as a
 * bare `<p>`, and a pattern for the bare form alone leaves the styled one behind as a gap.
 */
const EMPTY_PARAGRAPH = /<p\b[^>]*>\s*<\/p>/gi;

/**
 * Elements whose end separates words that would otherwise run together.
 *
 * `textContent` concatenates text without regard for layout, so
 * `<p>Load out</p><p>Check straps</p>` reads as `Load outCheck straps` unless each block is
 * given a trailing space first. Parsing alone does not supply this; it has to be asked for.
 */
const BLOCK_ELEMENTS = 'p, div, li, h1, h2, h3, h4, h5, h6, tr, td, th, blockquote';

const RUN_OF_WHITESPACE = /\s+/g;

/**
 * The same wording with its images removed, still as HTML.
 *
 * For a surface that renders HTML but has no `mediaResponses` map to resolve images with.
 * Paragraphs left empty by a removal go too, so the wording does not gain a blank gap
 * where a picture used to be.
 *
 * Edits the markup as text rather than parsing it, because the result is HTML that a
 * renderer receives: a parse and re-serialise round trip would also rewrite attribute
 * quoting, casing and entity spelling throughout wording this is not being asked to change.
 * `htmlAsPlainText` below returns text, so it has no such constraint and does parse.
 *
 * To show the images instead, the caller needs the record's media map: fetch the question
 * with `useQuestionQuery`, then use `replaceMediaIdsWithUrls` in place of this.
 */
export function htmlWithoutImages(html: string): string {
  if (!html) return '';

  return html.replace(IMAGE_TAG, '').replace(EMPTY_PARAGRAPH, '');
}

/**
 * The wording alone, with every tag dropped and every character reference resolved.
 *
 * For a surface that shows a string rather than rendering markup. Whitespace is collapsed to
 * one space, so wording authored across several paragraphs reads as one line.
 *
 * The browser's own parser does the reading, so the text matches what the reader would have
 * seen had the markup been rendered — every named entity, not a list of the common ones, and
 * each reference resolved exactly once so an ampersand a reference decodes to is never read
 * as the start of another. A malformed reference resolves the way it would on the page: past
 * Unicode's range or a lone surrogate half becomes the replacement character.
 *
 * Nothing is inserted into the live document. The markup is parsed into a detached one and
 * only its text is read, so a script or handler written into the wording never runs — and
 * the elements whose text is not wording are dropped before the reading.
 */
export function htmlAsPlainText(html: string): string {
  if (!html) return '';

  const { body } = new DOMParser().parseFromString(html, 'text/html');

  // These hold text that is not wording. Wording pasted out of a word processor carries a
  // `<style>` block, and its rules would otherwise be read out as part of the sentence.
  body.querySelectorAll('script, style').forEach((element) => element.remove());

  body.querySelectorAll('br').forEach((lineBreak) => lineBreak.replaceWith(' '));
  body.querySelectorAll(BLOCK_ELEMENTS).forEach((block) => block.append(' '));

  return (body.textContent ?? '').replace(RUN_OF_WHITESPACE, ' ').trim();
}
